import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdCheckCircle, MdScience, MdOpenInNew, MdDeleteOutline, MdHourglassEmpty, MdRadioButtonChecked } from 'react-icons/md'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { LuBuilding2, LuShare2, LuUser } from 'react-icons/lu'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import axiosClient from '../../../api/axios'
import { computeBlotCaseStatus, type BlotCaseStatus } from './blotCaseStatus'
import { IMMUNOBLOTS, moduleOfPathologyId, resolvePathologyLabel } from './ImmunoBlotSelection'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useMyProfileWithModules } from '../../../api/api_hooks/user/myProfile'
import { PlanType } from '../../../types/subscription'

export interface BlotAnalysis {
  id: string
  sampleId: string
  filename: string
  fileUrl: string
  totalLanes: number
  positiveLanes: number
  negativeLanes: number
  totalBands: number
  processingTimeSeconds: number
  createdAt: string
  updatedAt: string
  pathologyId?: string
  pathologyName?: string
  totalBlots?: number
  roi?: any
  blots?: any
  selectedPositiveControlId?: number | null
  selectedPositiveControl2Id?: number | null
  selectedPositiveControl3Id?: number | null
  detectionProfile?: string | null
  hiddenBlotIds?: number[]
  manualBlotResults?: Record<string, string> | null
  bandCalibration?: any
  /** 'prospective' (le fonctionnement habituel) ou 'retrospective'. */
  evaluationMode?: 'prospective' | 'retrospective'
  /** Lecture de paillasse figée (mode rétrospectif), par bandelette. */
  /** `blinding` : comment l'aveugle a été obtenu. `enforced` = le serveur a
   *  voilé les images, l'ordre des lectures est garanti par le logiciel.
   *  `declared` = l'opérateur affirme avoir lu en aveugle sans que le voile
   *  l'y contraigne. Les deux ne valent pas la même chose dans un dossier,
   *  d'où la nécessité de les séparer à l'analyse. */
  benchReading?: { blots?: Record<string, { bands?: string[]; conclusion?: string | null }>; lockedAt?: string; blinding?: 'enforced' | 'declared' } | null
  /** Lecture d'IRIS figée au dévoilement, par bandelette. */
  irisSnapshot?: { blots?: Record<string, { bands?: string[]; conclusion?: string | null }>; takenAt?: string } | null
  /** Present when the case was shared WITH the current user. */
  sharedBy?: { id: string; contactName: string | null; email: string; companyName: string | null } | null
  shareRole?: 'viewer' | 'editor'
  /** People the current user shared THIS (own) case with. Empty when not shared. */
  sharedWith?: Array<{ email: string; role: 'viewer' | 'editor'; sharedAt?: string | null }>
  /** Case owner (id + minimal identity) — used to label "Mes cas" vs "Autre pilote". */
  userId?: string
  user?: { id: string; contactName: string | null; email: string; companyName: string | null } | null
}

/**
 * Live lane stats from the case's own data. The stored totalLanes/positiveLanes/
 * negativeLanes columns are written once at analysis time and were never
 * recomputed on validation or ghost-deletion — so a validated case showed 0
 * positives/negatives, and (when the pipeline reported total_blots = 0) 0 blots,
 * despite having strips and results. Deriving them here keeps the card in sync
 * with what the biologist actually did. Falls back to the stored columns only
 * when `blots` wasn't returned, so nothing regresses.
 */
/** Les `blots` arrivent en tableau ou en JSON sérialisé selon le chemin. */
export function parsedBlots(a: BlotAnalysis): any[] {
  if (Array.isArray(a.blots)) return a.blots
  if (typeof a.blots === 'string') { try { return JSON.parse(a.blots) } catch { return [] } }
  return []
}

/**
 * Les pathologies d'UN cas : la sienne, plus celles de ses bandelettes.
 *
 * Une plaque multitest est enregistrée sous « DYNABLOT MULTITEST » mais porte
 * du Toxocara, du Schisto et de l'Echino à la fois : filtrer sur la seule
 * pathologie du cas la ferait disparaître de toutes les recherches qu'elle
 * contient — la mauvaise catégorisation qu'on vient de corriger côté serveur,
 * à ne pas réintroduire ici. Chaque bandelette vote avec son propre libellé,
 * résolu par le même dictionnaire de mots-clés que la galerie.
 */
/** Le module d'un cas, lu sur son entrée de catalogue. */
export function caseModule(a: BlotAnalysis): 'iris' | 'protis' {
  return moduleOfPathologyId(a.pathologyId)
}

export function casePathologyIds(a: BlotAnalysis): Set<string> {
  const ids = new Set<string>()
  if (a.pathologyId) ids.add(a.pathologyId)
  parsedBlots(a).forEach((b) => {
    const resolved = resolvePathologyLabel(b?.pathology_label)
    if (resolved) ids.add(resolved.id)
  })
  return ids
}

function laneStats(a: BlotAnalysis): { totalLanes: number; positiveLanes: number; negativeLanes: number } {
  if (a.blots == null) {
    return { totalLanes: a.totalLanes ?? 0, positiveLanes: a.positiveLanes ?? 0, negativeLanes: a.negativeLanes ?? 0 }
  }
  const blots = parsedBlots(a)
  const hidden = new Set(a.hiddenBlotIds ?? [])
  const visible = blots.filter((b) => b && !hidden.has(b.id))
  const results = a.manualBlotResults ?? {}
  let positiveLanes = 0
  let negativeLanes = 0
  for (const b of visible) {
    const r = results[String(b.id)]
    if (r === 'POSITIF') positiveLanes += 1
    else if (r === 'NÉGATIF') negativeLanes += 1
  }
  return { totalLanes: visible.length, positiveLanes, negativeLanes }
}

/**
 * Case status comes from the shared helper so this list and the home page's
 * "Tâches à traiter" can never disagree again — they used to, because the home
 * page keyed off `totalLanes` (always 0), so no case ever reached 'validated'.
 */
type CaseStatus = BlotCaseStatus
const computeCaseStatus = computeBlotCaseStatus

interface BlotRecentCasesProps {
  limit?: number
  onCaseSelect?: (analysis: BlotAnalysis) => void
  selectedCaseId?: string | null
  pathologyId?: string | null
  /** Show the owner (admin) + period filter bar. Off for the compact list. */
  showFilters?: boolean
  /**
   * Filtre par pathologie, contrôlé de l'extérieur (le registre d'évaluation,
   * où cliquer une ligne du tableau d'accord resserre la liste). Fourni, il
   * remplace l'état interne ; absent, le filtre vit ici. N'apparaît que sans
   * `pathologyId` — une liste déjà épinglée à un test n'a rien à choisir.
   */
  pathologyFilter?: string
  onPathologyFilterChange?: (id: string) => void
  /**
   * Remonte la liste telle qu'affichée, TOUS filtres appliqués — y compris
   * ceux résolus côté client (statut, mode, pathologie).
   */
  onVisibleChange?: (analyses: BlotAnalysis[]) => void
  /**
   * Remonte le périmètre CHARGÉ du module, AVANT les filtres de tri (statut,
   * mode, pathologie) mais après ceux qui définissent l'assiette (période,
   * scope admin). C'est sur lui que le registre agrège : trier la liste ne
   * doit pas faire bouger les scores d'accord — resserrer la période, si.
   */
  onLoadedChange?: (analyses: BlotAnalysis[]) => void
  /** Filtre de statut à l'ouverture — le panneau d'accueil envoie 'todo'
   *  (« + N autres cas ») pour atterrir directement sur le reste à faire. */
  initialStatusFilter?: 'all' | 'todo' | BlotCaseStatus
  /**
   * L'état de navigation posé sur « Accéder au cas » — le registre s'y
   * signe (`{ from: 'registry' }`) pour que la flèche de retour de
   * l'atelier ramène chez lui et non dans sa propre liste.
   */
  caseLinkState?: Record<string, unknown>
  /**
   * Demande au serveur les cas SANS leurs images (projection faite en base).
   * Pour le registre, qui charge jusqu'à cent cas d'un coup : 2,6 Mo mesurés
   * sur une seule plaque de 43 bandelettes en payload complet, ~1 Ko allégé.
   * La liste de l'atelier garde le payload complet — elle rouvre les cas
   * depuis lui.
   */
  light?: boolean
  /**
   * Restreint la liste aux cas d'UN module du Lab Studio. Le registre
   * d'évaluation est celui d'IRIS : un cas PROTIS y serait un intrus — autre
   * module, autre lecture, pas de catalogue de bandes — et polluerait autant
   * le filtre pathologie que les agrégats. Absent, rien n'est restreint.
   */
  module?: 'iris' | 'protis'
}

type OwnerScope = 'all' | 'mine' | 'others' | 'shared-by-me' | 'shared-with-me'

/**
 * Scopes available to everyone, not just admins — share-oriented ones, plus
 * 'mine' (own cases only, shared-with-me excluded; see recentCasesOwnerWhere/
 * STRICT_OWNER_SCOPES server-side, which already scope it identically for
 * admins and non-admins). 'others' stays excluded: a non-admin's owner-where
 * is always forced to their own userId regardless of scope, so 'others'
 * would silently return the same thing as 'mine' for them.
 */
const SHARE_SCOPES = new Set<OwnerScope>(['mine', 'shared-by-me', 'shared-with-me'])

/** Progress filter. Applied client-side since the status is derived, not stored. */
/** 'todo' = tout sauf validé (en attente + en cours) : le périmètre « il me
 *  reste du travail », celui que le panneau d'accueil pointe d'un clic. */
type StatusFilter = 'all' | 'todo' | BlotCaseStatus
// Clés i18n plutôt que libellés : ces constantes vivent hors composant, donc
// hors de portée de `t` — la traduction se fait au rendu.
const STATUS_FILTER_OPTIONS: [StatusFilter, string][] = [
  ['all', 'blotAnalysis.recentCasesList.allStatuses'],
  ['todo', 'blotAnalysis.recentCasesList.todo'],
  ['pending', 'blotAnalysis.recentCasesList.pending'],
  ['in-progress', 'blotAnalysis.recentCasesList.inProgress'],
  ['validated', 'blotAnalysis.recentCasesList.validated'],
]
/**
 * Mode d'évaluation. Il se choisit au dépôt et ne change plus ensuite, donc
 * c'est bien une propriété du cas et pas un état transitoire : retrouver ses
 * séries de recueil dans une liste qui mélange tout demandait jusqu'ici de les
 * ouvrir une par une.
 *
 * Filtré côté client, comme le statut : le serveur renvoie déjà
 * `evaluationMode` dans chaque cas. La réserve est la même — le tri porte sur
 * la page reçue, donc un cas rétrospectif plus ancien que `limit` n'y figure
 * pas. Élargir la période élargit aussi la pêche.
 */
type ModeFilter = 'all' | 'retrospective' | 'prospective'
const MODE_FILTER_OPTIONS: [ModeFilter, string][] = [
  ['all', 'Tous modes'],
  ['retrospective', 'Rétrospectif'],
  ['prospective', 'Prospectif'],
]
const PERIOD_OPTIONS: { days: number; label: string }[] = [
  { days: 0, label: 'Tout' },
  { days: 7, label: '7 j' },
  { days: 30, label: '30 j' },
  { days: 90, label: '90 j' },
]

// Visual meta per status — drives the card's left accent + pill.
const STATUS_META: Record<CaseStatus, { label: string; scheme: string; accent: string; icon: any }> = {
  validated: { label: 'blotAnalysis.recentCasesList.validated', scheme: 'green', accent: 'green', icon: MdCheckCircle },
  'in-progress': { label: 'blotAnalysis.recentCasesList.inProgress', scheme: 'orange', accent: 'orange', icon: MdRadioButtonChecked },
  pending: { label: 'blotAnalysis.recentCasesList.pending', scheme: 'gray', accent: 'gray', icon: MdHourglassEmpty },
}

/** Compact metric chip: colored dot + label + value. */
function StatChip({ dot, label, value }: { dot: string; label: string; value: number }) {
  return (
    <HStack spacing={1.5} bg="gray.50" borderRadius="md" px={2} py={1}>
      <Box w="6px" h="6px" borderRadius="full" bg={dot} />
      <Text fontSize="2xs" color="gray.500">
        {label}
      </Text>
      <Text fontSize="xs" fontWeight="700" color="gray.800">
        {value}
      </Text>
    </HStack>
  )
}

function CaseCard({
  analysis,
  isSelected,
  onCaseSelect,
  caseLinkState,
  onDeleted,
  aiAccessible,
  isAdmin,
  currentUserId,
}: {
  analysis: BlotAnalysis
  isSelected: boolean
  onCaseSelect?: (a: BlotAnalysis) => void
  caseLinkState?: Record<string, unknown>
  onDeleted: (id: string) => void
  aiAccessible: boolean
  isAdmin: boolean
  currentUserId?: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await axiosClient.delete(`/blot-analysis/case/${analysis.id}`)
      onAlertClose()
      onDeleted(analysis.id)
      queryClient.invalidateQueries({ queryKey: ['blotRecentCases'] })
      toast({ title: t('blotAnalysis.recentCasesList.caseDeleted'), status: 'success', duration: 2000, isClosable: true })
    } catch (_e) {
      toast({ title: t('blotAnalysis.recentCasesList.deleteError'), status: 'error', duration: 3000, isClosable: true })
    } finally {
      setIsDeleting(false)
    }
  }

  const shortId = analysis.id.slice(0, 8)
  const status = computeCaseStatus(analysis)
  const sm = STATUS_META[status]

  // Owner labelling (admin view lists every pilot's cases). Only shown to admins,
  // and never together with the "shared by" pill.
  const isMine = !!currentUserId && !!analysis.userId && analysis.userId === currentUserId
  const ownerName =
    analysis.user?.contactName ||
    analysis.user?.companyName ||
    analysis.user?.email?.split('@')[0] ||
    'autre pilote'
  const showOwnerBadge = isAdmin && !analysis.sharedBy

  return (
    <>
      <Box
        bg="white"
        borderRadius="xl"
        borderWidth="1px"
        borderColor={isSelected ? 'purple.300' : 'gray.100'}
        borderLeftWidth="3px"
        borderLeftColor={`${sm.accent}.400`}
        boxShadow={isSelected ? '0 0 0 3px var(--chakra-colors-purple-100)' : 'sm'}
        cursor={onCaseSelect ? 'pointer' : 'default'}
        onClick={() => onCaseSelect?.(analysis)}
        transition="all 0.18s ease"
        _hover={
          onCaseSelect
            ? { transform: 'translateY(-2px)', boxShadow: 'md', borderColor: isSelected ? 'purple.300' : 'gray.200' }
            : {}
        }
        overflow="hidden"
        position="relative"
      >
        <Box p={3.5}>
          <VStack align="stretch" spacing={2.5}>
            {/* Header */}
            <HStack justify="space-between" align="flex-start" spacing={2}>
              <HStack flex={1} minW={0} spacing={2.5} align="flex-start">
                <Box
                  w="32px"
                  h="32px"
                  borderRadius="lg"
                  bg={`${sm.accent}.50`}
                  display="grid"
                  sx={{ placeItems: 'center' }}
                  flexShrink={0}
                >
                  <Icon as={MdScience} color={`${sm.accent}.500`} boxSize={4} />
                </Box>
                <VStack align="flex-start" spacing={0.5} minW={0} flex={1}>
                  <HStack spacing={1.5} minW={0} w="100%">
                    <Text fontSize="sm" fontWeight="700" color="gray.800" noOfLines={1}>
                      {analysis.filename}
                    </Text>
                    <Badge
                      colorScheme={sm.scheme}
                      variant={status === 'validated' ? 'solid' : 'subtle'}
                      fontSize="2xs"
                      px={1.5}
                      py={0.5}
                      borderRadius="full"
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                      flexShrink={0}
                    >
                      <Icon as={sm.icon} boxSize={2.5} /> {t(sm.label)}
                    </Badge>
                  </HStack>
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontSize="2xs" color="gray.400" fontFamily="mono" letterSpacing="tight">
                      #{shortId}
                    </Text>
                    <Text fontSize="2xs" color="gray.400">
                      {format(new Date(analysis.updatedAt || analysis.createdAt), 'd MMM, HH:mm')}
                    </Text>
                    {showOwnerBadge && (
                      <Badge
                        colorScheme={isMine ? 'teal' : 'orange'}
                        variant="subtle"
                        fontSize="2xs"
                        px={1.5}
                        py={0.5}
                        borderRadius="md"
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Icon as={LuUser} boxSize={2.5} />
                        {isMine ? 'Mon cas' : `Pilote : ${ownerName}`}
                      </Badge>
                    )}
                    {analysis.sharedBy && (
                      <Tooltip label={analysis.sharedBy.email} hasArrow openDelay={300}>
                        <Badge
                          colorScheme="teal"
                          variant="subtle"
                          fontSize="2xs"
                          px={1.5}
                          py={0.5}
                          borderRadius="md"
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                          maxW="230px"
                        >
                          <Icon as={LuShare2} boxSize={2.5} flexShrink={0} />
                          <Text as="span" isTruncated>
                            Partagé par {analysis.sharedBy.email}
                          </Text>
                        </Badge>
                      </Tooltip>
                    )}
                    {!analysis.sharedBy && (analysis.sharedWith?.length ?? 0) > 0 && (
                      <Tooltip
                        label={analysis.sharedWith!
                          .map((s) => `${s.email} — ${s.role === 'editor' ? 'collaborateur' : 'lecture seule'}`)
                          .join('\n')}
                        hasArrow
                        openDelay={300}
                        whiteSpace="pre-line"
                      >
                        <Badge
                          colorScheme="purple"
                          variant="subtle"
                          fontSize="2xs"
                          px={1.5}
                          py={0.5}
                          borderRadius="md"
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                          maxW="250px"
                        >
                          <Icon as={LuShare2} boxSize={2.5} flexShrink={0} />
                          <Text as="span" isTruncated>
                            Partagé avec {analysis.sharedWith![0].email}
                            {analysis.sharedWith!.length > 1 ? ` +${analysis.sharedWith!.length - 1}` : ''}
                          </Text>
                        </Badge>
                      </Tooltip>
                    )}
                  </HStack>
                </VStack>
              </HStack>
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <IconButton
                    aria-label="Options"
                    icon={<BsThreeDotsVertical />}
                    size="xs"
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: 'gray.700', bg: 'gray.100' }}
                    onClick={(e) => e.stopPropagation()}
                    flexShrink={0}
                  />
                </PopoverTrigger>
                <Portal>
                  <PopoverContent w="160px" boxShadow="md" onClick={(e) => e.stopPropagation()}>
                    <PopoverBody p={1}>
                      <VStack spacing={0} align="stretch">
                        <Button
                          size="sm"
                          variant="ghost"
                          justifyContent="flex-start"
                          leftIcon={<Icon as={MdOpenInNew} />}
                          fontWeight={400}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/lab/blot-analysis/${analysis.id}`, { state: caseLinkState })
                          }}
                        >
                          Accéder au cas
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          justifyContent="flex-start"
                          leftIcon={<Icon as={MdDeleteOutline} color="red.400" />}
                          fontWeight={400}
                          color="red.500"
                          _hover={{ bg: 'red.50' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            onAlertOpen()
                          }}
                        >
                          Supprimer
                        </Button>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </HStack>

            {/* Metrics — derived live from the case data (see laneStats). */}
            {(() => {
              const stats = laneStats(analysis)
              return (
                <HStack spacing={2} flexWrap="wrap">
                  <StatChip dot="purple.400" label={t('blotAnalysis.recentCasesList.totalLanes')} value={stats.totalLanes} />
                  <StatChip dot="blue.400" label={t('blotAnalysis.recentCasesList.bands')} value={analysis.totalBands} />
                  <StatChip dot="green.400" label={t('blotAnalysis.recentCasesList.positive')} value={stats.positiveLanes} />
                  <StatChip dot="red.400" label={t('blotAnalysis.recentCasesList.negative')} value={stats.negativeLanes} />
                </HStack>
              )
            })()}

            {/* Footer */}
            {(analysis.processingTimeSeconds || analysis.detectionProfile) && (
              <HStack justify="space-between" align="center" pt={2} borderTop="1px solid" borderColor="gray.100">
                {analysis.processingTimeSeconds ? (
                  <Text fontSize="2xs" color="gray.400">
                    {t('blotAnalysis.recentCasesList.processedIn')} {analysis.processingTimeSeconds.toFixed(2)}s
                  </Text>
                ) : (
                  <Box />
                )}
                {analysis.detectionProfile && (
                  <HStack spacing={1}>
                    <Icon as={LuBuilding2} boxSize={3} color="purple.400" />
                    <Text fontSize="2xs" color="purple.600" fontWeight="700" letterSpacing="wide">
                      {analysis.detectionProfile.toUpperCase()}
                    </Text>
                  </HStack>
                )}
              </HStack>
            )}
          </VStack>
        </Box>
      </Box>

      {/* Delete confirmation */}
      <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef} onClose={onAlertClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Supprimer le cas
            </AlertDialogHeader>
            <AlertDialogBody>
              Êtes-vous sûr de vouloir supprimer ce cas ?
              <Text fontSize="xs" color="gray.500" mt={1} fontFamily="mono">
                {analysis.filename} — #{shortId}
              </Text>
              <Text fontSize="sm" color="red.500" mt={2}>
                Cette action est irréversible.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onAlertClose} isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
              >
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

export default function BlotRecentCases({
  limit = 10,
  onCaseSelect,
  selectedCaseId,
  pathologyId,
  showFilters = true,
  pathologyFilter: pathologyFilterProp,
  onPathologyFilterChange,
  onVisibleChange,
  onLoadedChange,
  initialStatusFilter,
  module,
  caseLinkState,
  light = false,
}: BlotRecentCasesProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: currentUser } = useMyProfileWithModules()
  const userPlan = currentUser?.plan
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin'
  // Same gating as BlotAnalysisWorkspace: AI verdicts unlocked on Premium/Enterprise (or admin)
  const aiAccessible = isAdmin || userPlan === PlanType.PREMIUM || userPlan === PlanType.ENTERPRISE

  // Filters (server-side): owner scope is admin-only; period applies to everyone.
  const [scope, setScope] = useState<OwnerScope>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatusFilter ?? 'all')
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all')
  const [days, setDays] = useState<number>(0)
  // Par CHU (profil de détection du cas) — admin seulement : ses cas et ceux
  // de ses pilotes traversent plusieurs CHU, ceux d'un pilote n'en ont qu'un.
  const [chuFilter, setChuFilter] = useState<string>('all')
  // Contrôlé quand le parent fournit le couple valeur/rappel, interne sinon.
  const [pathologyFilterState, setPathologyFilterState] = useState<string>('all')
  const pathologyFilter = onPathologyFilterChange
    ? (pathologyFilterProp ?? 'all')
    : pathologyFilterState
  const setPathologyFilter = onPathologyFilterChange ?? setPathologyFilterState
  // Owner scopes (mine/others) stay admin-only; the share scopes are for everyone.
  const effectiveScope: OwnerScope = isAdmin || SHARE_SCOPES.has(scope) ? scope : 'all'

  const queryKey = ['blotRecentCases', limit, pathologyId, effectiveScope, days, light]
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: limit.toString() })
      if (pathologyId) params.append('pathologyId', pathologyId)
      if (effectiveScope !== 'all') params.append('scope', effectiveScope)
      if (days > 0) params.append('days', String(days))
      if (light) params.append('light', '1')
      const response = await axiosClient.get(`/blot-analysis/recent-cases?${params.toString()}`)
      return response.data
    },
    refetchInterval: 30000,
  })

  const handleDeleted = (id: string) => {
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old) return old
      return { ...old, analyses: old.analyses.filter((a: BlotAnalysis) => a.id !== id) }
    })
  }

  // Status is derived client-side (computeCaseStatus), so this filter is applied
  // here rather than server-side.
  const allAnalyses: BlotAnalysis[] = useMemo(() => data?.analyses || [], [data])
  // Le périmètre CHARGÉ du module, avant les filtres de tri (statut, mode,
  // pathologie) : c'est l'assiette que le registre agrège — ses chiffres ne
  // doivent pas bouger quand on trie la liste, seulement quand l'assiette
  // change (période, scope admin). Mémoïsé pour la même raison qu'`analyses`.
  const loadedForModule: BlotAnalysis[] = useMemo(
    () => allAnalyses.filter((a) => !module || caseModule(a) === module),
    [allAnalyses, module],
  )
  // Mémoïsé pour que `onVisibleChange` ne tire qu'aux vrais changements : un
  // tableau reconstruit à chaque rendu ferait boucler le parent qui le range
  // dans un état.
  const analyses: BlotAnalysis[] = useMemo(() => loadedForModule
    .filter((a) => {
      if (statusFilter === 'all') return true
      if (statusFilter === 'todo') return computeCaseStatus(a) !== 'validated'
      return computeCaseStatus(a) === statusFilter
    })
    // Un cas sans mode enregistré date d'avant le rétrospectif : il est
    // prospectif, c'est le fonctionnement qui a toujours été le sien.
    .filter((a) => modeFilter === 'all'
      || ((a as any).evaluationMode ?? 'prospective') === modeFilter)
    .filter((a) => pathologyFilter === 'all' || casePathologyIds(a).has(pathologyFilter))
    .filter((a) => chuFilter === 'all' || (a.detectionProfile || 'default') === chuFilter),
  [loadedForModule, statusFilter, modeFilter, pathologyFilter, chuFilter])

  useEffect(() => { onVisibleChange?.(analyses) }, [analyses, onVisibleChange])
  useEffect(() => { onLoadedChange?.(loadedForModule) }, [loadedForModule, onLoadedChange])

  // Les pathologies réellement présentes dans la page chargée — pas tout le
  // catalogue, qui proposerait des filtres à résultat vide. La valeur choisie
  // reste listée même quand un autre filtre l'a vidée, sinon le sélecteur
  // afficherait du blanc sans moyen d'en sortir.
  //
  // Deux entrées du catalogue n'y figurent jamais :
  //  - celles d'un AUTRE module (PROTIS) : autre atelier, autre registre ;
  //  - la plaque « DYNABLOT MULTITEST » : c'est un format de plaque, pas une
  //    pathologie. Ses cas se retrouvent par ce qu'ils CONTIENNENT — chaque
  //    bandelette vote avec son propre libellé dans `casePathologyIds` —
  //    donc filtrer « Toxocara » ramène bien la plaque qui en porte.
  const pathologyOptions = useMemo(() => {
    if (!showFilters || pathologyId) return []
    const present = new Set<string>()
    allAnalyses.forEach((a) => casePathologyIds(a).forEach((id) => present.add(id)))
    return IMMUNOBLOTS
      .filter((b) => (b.module ?? 'iris') === (module ?? 'iris'))
      .filter((b) => b.id !== 'dynablot-multitest')
      .filter((b) => present.has(b.id) || b.id === pathologyFilter)
  }, [allAnalyses, showFilters, pathologyId, pathologyFilter, module])

  // Les CHU réellement présents dans la page chargée (profil de détection des
  // cas), avec leur compte — le sélecteur n'apparaît que pour un admin et
  // seulement quand il y a de quoi filtrer. « chu-nancy » → « CHU Nancy ».
  const chuLabel = (id: string): string => {
    if (id === 'default') return 'Défaut'
    if (id === 'aphp') return 'AP-HP'
    if (id === 'protis') return 'PROTIS'
    return id.split('-').map((w) => (w === 'chu' ? 'CHU' : w.charAt(0).toUpperCase() + w.slice(1))).join(' ')
  }
  const chuOptions = useMemo(() => {
    if (!showFilters || !isAdmin) return []
    const counts = new Map<string, number>()
    loadedForModule.forEach((a) => {
      const p = a.detectionProfile || 'default'
      counts.set(p, (counts.get(p) ?? 0) + 1)
    })
    return Array.from(counts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((x, y) => y.count - x.count)
  }, [loadedForModule, showFilters, isAdmin])

  const filterBar = showFilters && (
    <HStack justify="space-between" flexWrap="wrap" gap={2}>
      {(
        <HStack spacing={1} bg="gray.100" p={1} borderRadius="lg" flexWrap="wrap">
          {([
            ['all', 'Tous'],
            // 'mine' (own cases only, shared-with-me excluded — see
            // STRICT_OWNER_SCOPES server-side) is meaningful for ANY user:
            // a collaborateur can have both cases they ran themselves and
            // cases shared with them, same as an admin. 'others' stays
            // admin-only — recentCasesOwnerWhere always scopes a non-admin
            // to their own userId regardless of scope, so it would silently
            // behave exactly like 'mine' for them, which is confusing.
            ['mine', t('blotAnalysis.recentCasesList.myCases')],
            ...(isAdmin ? ([['others', t('blotAnalysis.recentCasesList.otherPilots')]] as [OwnerScope, string][]) : []),
            ['shared-by-me', t('blotAnalysis.recentCasesList.sharedByMe')],
            ['shared-with-me', t('blotAnalysis.recentCasesList.sharedWithMe')],
          ] as [OwnerScope, string][]).map(([val, label]) => (
            <Button
              key={val}
              size="xs"
              variant={scope === val ? 'solid' : 'ghost'}
              colorScheme={scope === val ? 'teal' : 'gray'}
              color={scope === val ? undefined : 'gray.600'}
              borderRadius="md"
              onClick={() => setScope(val)}
            >
              {label}
            </Button>
          ))}
        </HStack>
      )}
      <HStack spacing={1} bg="gray.100" p={1} borderRadius="lg" flexWrap="wrap">
        {STATUS_FILTER_OPTIONS.map(([val, label]) => (
          <Button
            key={val}
            size="xs"
            variant={statusFilter === val ? 'solid' : 'ghost'}
            colorScheme={statusFilter === val ? 'blue' : 'gray'}
            color={statusFilter === val ? undefined : 'gray.600'}
            borderRadius="md"
            onClick={() => setStatusFilter(val)}
          >
            {t(label)}
          </Button>
        ))}
      </HStack>
      <HStack spacing={1} bg="gray.100" p={1} borderRadius="lg" flexWrap="wrap">
        {MODE_FILTER_OPTIONS.map(([val, label]) => (
          <Button
            key={val}
            size="xs"
            variant={modeFilter === val ? 'solid' : 'ghost'}
            colorScheme={modeFilter === val ? 'purple' : 'gray'}
            color={modeFilter === val ? undefined : 'gray.600'}
            borderRadius="md"
            onClick={() => setModeFilter(val)}
          >
            {label}
          </Button>
        ))}
      </HStack>
      {/* Par pathologie — en liste déroulante : le catalogue est trop long
          pour une rangée de boutons. Seules les pathologies présentes dans la
          page chargée sont proposées. */}
      {!pathologyId && pathologyOptions.length > 0 && (
        <Select
          size="sm"
          w="auto"
          maxW="280px"
          bg="gray.100"
          border="none"
          borderRadius="lg"
          fontWeight="600"
          color={pathologyFilter === 'all' ? 'gray.600' : 'teal.700'}
          value={pathologyFilter}
          onChange={(e) => setPathologyFilter(e.target.value)}
        >
          <option value="all">Toutes pathologies</option>
          {pathologyOptions.map((b) => (
            <option key={b.id} value={b.id}>{b.displayName ?? b.name}</option>
          ))}
        </Select>
      )}
      {/* Par CHU (admin) — se combine avec le reste : « Autres pilotes » +
          Aspergillus + CHU Bordeaux, par exemple. */}
      {chuOptions.length > 1 && (
        <Select
          size="sm"
          w="auto"
          maxW="220px"
          bg="gray.100"
          border="none"
          borderRadius="lg"
          fontWeight="600"
          color={chuFilter === 'all' ? 'gray.600' : 'purple.700'}
          value={chuFilter}
          onChange={(e) => setChuFilter(e.target.value)}
        >
          <option value="all">Tous les CHU</option>
          {chuOptions.map((c) => (
            <option key={c.key} value={c.key}>{chuLabel(c.key)} ({c.count})</option>
          ))}
        </Select>
      )}
      <HStack spacing={1} bg="gray.100" p={1} borderRadius="lg">
        {PERIOD_OPTIONS.map(({ days: d, label }) => (
          <Button
            key={d}
            size="xs"
            variant={days === d ? 'solid' : 'ghost'}
            colorScheme={days === d ? 'purple' : 'gray'}
            color={days === d ? undefined : 'gray.600'}
            borderRadius="md"
            onClick={() => setDays(d)}
          >
            {label}
          </Button>
        ))}
      </HStack>
    </HStack>
  )

  let body: JSX.Element
  if (isLoading) {
    body = (
      <Box bg="white" p={6} borderRadius="lg" shadow="sm" textAlign="center">
        <Spinner size="md" color="purple.500" />
        <Text mt={2} fontSize="sm" color="gray.600">
          {t('blotAnalysis.recentCasesList.loadingCases')}
        </Text>
      </Box>
    )
  } else if (error) {
    body = (
      <Box bg="white" p={6} borderRadius="lg" shadow="sm">
        <Text color="red.500">{t('blotAnalysis.recentCasesList.errorLoadingCases')}</Text>
      </Box>
    )
  } else if (analyses.length === 0) {
    body = (
      <Box bg="white" p={6} borderRadius="lg" shadow="sm" textAlign="center">
        <Icon as={MdScience} boxSize={12} color="gray.300" />
        <Text mt={2} color="gray.500" fontSize="sm">
          {(effectiveScope !== 'all' || days > 0 || modeFilter !== 'all')
            ? t('blotAnalysis.recentCasesList.noMatch')
            : t('blotAnalysis.recentCasesList.noAnalysesYet')}
        </Text>
        {!(effectiveScope !== 'all' || days > 0) && (
          <Text fontSize="xs" color="gray.400">
            {t('blotAnalysis.recentCasesList.uploadToGetStarted')}
          </Text>
        )}
      </Box>
    )
  } else {
    body = (
      <VStack spacing={3} align="stretch">
        {analyses.map((analysis) => (
          <CaseCard
            key={analysis.id}
            analysis={analysis}
            isSelected={selectedCaseId === analysis.id}
            onCaseSelect={onCaseSelect}
            caseLinkState={caseLinkState}
            onDeleted={handleDeleted}
            aiAccessible={aiAccessible}
            isAdmin={isAdmin}
            currentUserId={currentUser?.id}
          />
        ))}
      </VStack>
    )
  }

  return (
    <VStack spacing={3} align="stretch">
      {filterBar}
      {body}
    </VStack>
  )
}
